import {
  useCreateLinkTokenLazyQuery,
  useLinkPlaidAccountMutation,
} from '@transactions/graphql';
import * as React from 'react';
import {PlaidEmbeddedLink} from 'react-plaid-link';
import {Button} from 'src/Button';
import Checkbox from 'src/Checkbox/Checkbox';
import {Modal} from 'src/Modal';
import type {PlaidLinkOnSuccessMetadata} from 'react-plaid-link';
import type {SystemProps} from 'src/Modals';

export type ConnectPlaidModalUserProps = {
  onSuccess?: () => void;
};

type Step = 'product-selection' | 'plaid-link';

export const ConnectPlaidModal = (
  props: ConnectPlaidModalUserProps & SystemProps,
) => {
  const [step, setStep] = React.useState<Step>('product-selection');
  const [transactions, setTransactions] = React.useState(true);
  const [investments, setInvestments] = React.useState(false);
  const [linkToken, setLinkToken] = React.useState<string | null>(null);

  const [getLinkToken, {loading: linkTokenLoading}] =
    useCreateLinkTokenLazyQuery();
  const [linkAccount] = useLinkPlaidAccountMutation();

  const selectedProducts = React.useMemo(() => {
    const products: string[] = [];
    if (transactions) products.push('transactions');
    if (investments) products.push('investments');
    return products;
  }, [transactions, investments]);

  const handleContinue = () => {
    getLinkToken({
      variables: {
        env: 'production',
        products: selectedProducts,
      },
    }).then((result) => {
      if (result.data?.createLinkToken) {
        setLinkToken(result.data.createLinkToken);
        setStep('plaid-link');
      }
    });
  };

  const handleBack = () => {
    setStep('product-selection');
    setLinkToken(null);
  };

  const handlePlaidSuccess = (
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => {
    linkAccount({
      variables: {
        plaidEnvironment: 'production',
        token: publicToken,
        institutionId: metadata.institution?.institution_id,
        institutionName: metadata.institution?.name,
        products: selectedProducts,
      },
    }).then(() => {
      props.onSuccess?.();
      props.onRequestClose();
    });
  };

  const handlePlaidExit = () => {
    setStep('product-selection');
    setLinkToken(null);
  };

  if (step === 'product-selection') {
    const canContinue = transactions || investments;
    const footer = (
      <>
        <Button variant="outline" onClick={props.onRequestClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!canContinue || linkTokenLoading}
          onClick={handleContinue}
        >
          {linkTokenLoading ? 'Loading...' : 'Continue'}
        </Button>
      </>
    );

    return (
      <Modal
        onRequestClose={props.onRequestClose}
        header="Connect account with Plaid"
        footer={footer}
      >
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground">
            Select which Plaid products to authorize for this connection.
          </p>
          <div className="flex flex-col gap-3">
            <div
              className="flex cursor-pointer items-center gap-3"
              onClick={() => setTransactions(!transactions)}
            >
              <Checkbox value={transactions} onChange={setTransactions} />
              <div className="flex flex-col">
                <span className="font-medium">Transactions</span>
                <span className="text-muted-foreground text-sm">
                  Sync checking, savings, and credit card transactions
                </span>
              </div>
            </div>
            <div
              className="flex cursor-pointer items-center gap-3"
              onClick={() => setInvestments(!investments)}
            >
              <Checkbox value={investments} onChange={setInvestments} />
              <div className="flex flex-col">
                <span className="font-medium">Investments</span>
                <span className="text-muted-foreground text-sm">
                  Sync brokerage, 401k, and IRA account holdings
                </span>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  // Step: plaid-link
  const footer = (
    <Button variant="outline" onClick={handleBack}>
      Back
    </Button>
  );

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      header="Connect account with Plaid"
      footer={footer}
      size="lg"
    >
      <div className="h-[500px] w-full">
        {linkToken && (
          <PlaidEmbeddedLink
            token={linkToken}
            onSuccess={handlePlaidSuccess}
            onExit={handlePlaidExit}
            style={{height: '100%', width: '100%'}}
          />
        )}
      </div>
    </Modal>
  );
};
